/**
 * Ride Data Exporter
 * 
 * Transforms Atlas ride data (RideSchema) into UAT/Production API upload formats.
 * Handles timezone conversion, address formatting, and passenger data transformation.
 * 
 * @module rideDataExporter
 */

import type { RideSchema, PassengerType } from '../types/simulation';
import type { LocationSearchResult } from './rideUatClient';
import { applyRideAddressPlaceholders } from '../utils/rides/addressPlaceholders';

/**
 * Validated ride data ready for export
 */
export interface ValidatedRideData {
  ride: RideSchema;
  pickupLocation: LocationSearchResult;
  dropoffLocation: LocationSearchResult;
  userId: string;
  tripType: 'leaveAt' | 'arriveBy';
  time: string; // ISO 8601 with timezone offset
}

/**
 * Ride export result
 */
export interface RideExportResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  validatedData?: ValidatedRideData;
}

/**
 * Normalize ride passenger data
 * 
 * Ensures passengers array is valid and all entries have number_of_passengers >= 1
 * 
 * @param ride - RideSchema to normalize
 * @returns Normalized ride with valid passenger data
 */
function normalizeRidePassengers(ride: RideSchema): RideSchema {
  // If no passengers or empty array, add default
  if (!ride.passengers || ride.passengers.length === 0) {
    return {
      ...ride,
      passengers: [{ type: 'standard', number_of_passengers: 1 }],
    };
  }
  
  // Filter out invalid entries and ensure counts are >= 1
  const normalizedPassengers = ride.passengers
    .filter(p => p && p.type) // Remove entries without type
    .map(p => ({
      ...p,
      number_of_passengers: Math.max(1, p.number_of_passengers ?? 1), // Ensure at least 1
    }))
    .filter(p => p.number_of_passengers > 0); // Remove entries with 0 or negative counts
  
  // If all passengers were invalid, add default
  if (normalizedPassengers.length === 0) {
    return {
      ...ride,
      passengers: [{ type: 'standard', number_of_passengers: 1 }],
    };
  }
  
  return {
    ...ride,
    passengers: normalizedPassengers,
  };
}

/**
 * Validate ride data before export
 * 
 * @param ride - RideSchema to validate
 * @returns Validation result
 */
export function validateRideExport(ride: RideSchema): RideExportResult {
  // Normalize passenger data first
  const normalizedRide = normalizeRidePassengers(ride);
  
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate coordinates (using normalized ride)
  if (
    normalizedRide.from_latitude === undefined ||
    normalizedRide.from_longitude === undefined ||
    normalizedRide.to_latitude === undefined ||
    normalizedRide.to_longitude === undefined
  ) {
    errors.push('Ride is missing required coordinates');
  } else {
    // Validate coordinate ranges
    if (normalizedRide.from_latitude < -90 || normalizedRide.from_latitude > 90) {
      errors.push('Invalid pickup latitude');
    }
    if (normalizedRide.from_longitude < -180 || normalizedRide.from_longitude > 180) {
      errors.push('Invalid pickup longitude');
    }
    if (normalizedRide.to_latitude < -90 || normalizedRide.to_latitude > 90) {
      errors.push('Invalid dropoff latitude');
    }
    if (normalizedRide.to_longitude < -180 || normalizedRide.to_longitude > 180) {
      errors.push('Invalid dropoff longitude');
    }
  }

  // Validate passengers (using normalized ride)
  if (!normalizedRide.passengers || normalizedRide.passengers.length === 0) {
    errors.push('Ride must have at least one passenger');
  } else {
    normalizedRide.passengers.forEach((passenger, index) => {
      if (!passenger.type) {
        errors.push(`Passenger ${index + 1} is missing type`);
      }
      if (passenger.number_of_passengers === undefined || passenger.number_of_passengers < 1) {
        errors.push(`Passenger ${index + 1} must have at least 1 passenger`);
      }
    });
  }

  // Validate request time
  if (!normalizedRide.request_time) {
    errors.push('Ride is missing request time');
  } else {
    // Validate time format (should be ISO 8601 or similar)
    try {
      const timeDate = new Date(normalizedRide.request_time);
      if (isNaN(timeDate.getTime())) {
        errors.push('Invalid request time format');
      }
    } catch {
      errors.push('Invalid request time format');
    }
  }

  // Validate user ID
  if (!normalizedRide.user_id && !normalizedRide.external_user_reference_id) {
    errors.push('Ride is missing user ID');
  }

  // Warnings
  if (!normalizedRide.from_address) {
    warnings.push('Ride is missing pickup address');
  }
  if (!normalizedRide.to_address) {
    warnings.push('Ride is missing dropoff address');
  }
  if (!normalizedRide.request_type) {
    warnings.push('Ride is missing request type (will default to leaveAt)');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Transform ride to API payload format
 * 
 * @param ride - RideSchema to transform
 * @param pickupLocation - Searched pickup location
 * @param dropoffLocation - Searched dropoff location
 * @param userId - User ID for the ride
 * @param tripType - Trip type (leaveAt or arriveBy)
 * @param time - Formatted time string (ISO 8601 with timezone offset)
 * @returns Transformed ride payload
 */
export function transformRideToPayload(
  ride: RideSchema,
  pickupLocation: LocationSearchResult,
  dropoffLocation: LocationSearchResult,
  userId: string,
  tripType: 'leaveAt' | 'arriveBy',
  time: string
): {
  departureAddress: {
    label: string;
    latitude: number;
    longitude: number;
    placeId: string | null;
  };
  arrivalAddress: {
    label: string;
    latitude: number;
    longitude: number;
    placeId: string | null;
  };
  time: string;
  tripPassengers: Array<{
    id: string;
    seatType: string;
    concessionType: string;
    luggage: any[];
  }>;
  tripType: 'leaveAt' | 'arriveBy';
  userId: string;
  allowedServices?: string[]; // Optional: Array of service UUIDs
} {
  // Normalize passenger data before transforming
  const normalizedRide = applyRideAddressPlaceholders(normalizeRidePassengers(ride), 1);
  
  // Transform passengers - match Python script exactly
  // Python uses fixed IDs: wheelchair_1 (only one), standard_1, standard_2, etc.
  const tripPassengers: Array<{
    id: string;
    seatType: string;
    concessionType: string;
    luggage: any[];
  }> = [];

  // Track if we've added a wheelchair passenger (Python only allows one: wheelchair_1)
  let hasWheelchair = false;
  let standardCount = 0;
  
  normalizedRide.passengers.forEach((passenger) => {
    const seatType = passenger.type === 'wheelchair' || passenger.type === 'Wheelchair'
      ? 'wheelchair'
      : 'standard';
    
    const count = passenger.number_of_passengers || 1;
    
    if (seatType === 'wheelchair' && !hasWheelchair) {
      // Only add one wheelchair passenger with ID wheelchair_1 (matching Python)
      tripPassengers.push({
        id: 'wheelchair_1',
        seatType: 'wheelchair',
        concessionType: '',
        luggage: [],
      });
      hasWheelchair = true;
      
      // If count > 1, add remaining as standard passengers
      for (let i = 1; i < count; i++) {
        standardCount++;
        tripPassengers.push({
          id: `standard_${standardCount}`,
          seatType: 'standard',
          concessionType: '',
          luggage: [],
        });
      }
    } else {
      // Standard passengers: standard_1, standard_2, etc.
      for (let i = 0; i < count; i++) {
        standardCount++;
        tripPassengers.push({
          id: `standard_${standardCount}`,
          seatType: 'standard',
          concessionType: '',
          luggage: [],
        });
      }
    }
  });

  // If no passengers, add one standard passenger
  if (tripPassengers.length === 0) {
    tripPassengers.push({
      id: 'standard_1',
      seatType: 'standard',
      concessionType: '',
      luggage: [],
    });
  }

  // Build address objects - match Python script exactly
  // Python always includes placeId in the dict (None becomes null in JSON)
  const departureAddress: {
    label: string;
    latitude: number;
    longitude: number;
    placeId: string | null;
  } = {
    label: normalizedRide.from_address || pickupLocation.label || 'Pickup Location',
    latitude: pickupLocation.latitude,
    longitude: pickupLocation.longitude,
    placeId: pickupLocation.placeId ?? null, // Always include (null if missing, matching Python None -> null)
  };

  const arrivalAddress: {
    label: string;
    latitude: number;
    longitude: number;
    placeId: string | null;
  } = {
    label: normalizedRide.to_address || dropoffLocation.label || 'Dropoff Location',
    latitude: dropoffLocation.latitude,
    longitude: dropoffLocation.longitude,
    placeId: dropoffLocation.placeId ?? null, // Always include (null if missing, matching Python None -> null)
  };

  return {
    departureAddress,
    arrivalAddress,
    time,
    tripPassengers,
    tripType,
    userId,
    // allowedServices will be added by the upload hook after fetching services
  };
}

/**
 * Format time to ISO 8601 UTC format with Z
 * 
 * Treats time as timezone-agnostic (matching Python implementation).
 * If the time is in the past, adjusts to tomorrow at the same time.
 * Converts the local time in the target timezone to UTC and formats with Z.
 * 
 * @param timeString - Time string (ISO 8601, timezone-agnostic)
 * @param timezone - Target timezone (e.g., "Australia/Sydney", "America/Sao_Paulo")
 * @returns Formatted time string in UTC with Z (e.g., "2025-12-13T10:55:00.000Z")
 */
export function formatTimeWithTimezone(timeString: string, timezone: string, serviceDate?: string): string {
  try {
    // Parse the time string as timezone-agnostic
    // Remove any timezone info first to treat it as naive datetime
    const cleanTimeString = timeString.replace(/[Z+-]\d{2}:?\d{2}$|[Z]$/, '');
    
    // Extract components directly from string to avoid timezone interpretation
    // Format: YYYY-MM-DDTHH:mm:ss or YYYY-MM-DDTHH:mm:ss.SSS
    const datetimeMatch = cleanTimeString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?/);
    
    if (!datetimeMatch) {
      console.warn('[formatTimeWithTimezone] Failed to parse time string:', timeString);
      return timeString;
    }

    // Extract time components directly from string (timezone-agnostic)
    let year = parseInt(datetimeMatch[1], 10);
    let month = parseInt(datetimeMatch[2], 10);
    let day = parseInt(datetimeMatch[3], 10);
    let hour = parseInt(datetimeMatch[4], 10);
    let minute = parseInt(datetimeMatch[5], 10);
    let second = parseInt(datetimeMatch[6], 10);

    // If serviceDate is provided, override the date part while keeping the time
    if (serviceDate && /^\d{4}-\d{2}-\d{2}$/.test(serviceDate)) {
      const serviceDateMatch = serviceDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (serviceDateMatch) {
        year = parseInt(serviceDateMatch[1], 10);
        month = parseInt(serviceDateMatch[2], 10);
        day = parseInt(serviceDateMatch[3], 10);
        console.log(`[formatTimeWithTimezone] Using service date ${serviceDate}, keeping time ${hour}:${minute}:${second}`);
      }
    }

    // Get current time as timezone-agnostic for comparison
    // Use UTC methods to avoid local timezone interpretation
    const now = new Date();
    const nowYear = now.getUTCFullYear();
    const nowMonth = now.getUTCMonth() + 1;
    const nowDay = now.getUTCDate();
    const nowHour = now.getUTCHours();
    const nowMinute = now.getUTCMinutes();
    const nowSecond = now.getUTCSeconds();

    // Check if the time is in the past (timezone-agnostic comparison)
    const isPast = 
      year < nowYear ||
      (year === nowYear && month < nowMonth) ||
      (year === nowYear && month === nowMonth && day < nowDay) ||
      (year === nowYear && month === nowMonth && day === nowDay && hour < nowHour) ||
      (year === nowYear && month === nowMonth && day === nowDay && hour === nowHour && minute < nowMinute) ||
      (year === nowYear && month === nowMonth && day === nowDay && hour === nowHour && minute === nowMinute && second <= nowSecond);

    // If time is in the past, adjust to tomorrow at the same time (timezone-agnostic)
    let finalYear = year;
    let finalMonth = month;
    let finalDay = day;
    let finalHour = hour;
    let finalMinute = minute;
    let finalSecond = second;
    
    if (isPast) {
      // Set to tomorrow at the same time (matching Python ensure_future_time)
      // Calculate tomorrow using UTC to avoid timezone issues
      const tomorrow = new Date(Date.UTC(nowYear, nowMonth - 1, nowDay + 1));
      
      finalYear = tomorrow.getUTCFullYear();
      finalMonth = tomorrow.getUTCMonth() + 1;
      finalDay = tomorrow.getUTCDate();
      finalHour = hour; // Keep the same time
      finalMinute = minute;
      finalSecond = second;
      
      console.log(`[formatTimeWithTimezone] Time was in the past, adjusted to tomorrow: ${finalYear}-${finalMonth.toString().padStart(2, '0')}-${finalDay.toString().padStart(2, '0')} ${finalHour.toString().padStart(2, '0')}:${finalMinute.toString().padStart(2, '0')}`);
    }

    // Now we need to find the UTC time that gives us finalYear-finalMonth-finalDay finalHour:finalMinute:finalSecond in target timezone
    // Create a date string and use it to find the right UTC time
    const targetDateStr = `${finalYear}-${finalMonth.toString().padStart(2, '0')}-${finalDay.toString().padStart(2, '0')}T${finalHour.toString().padStart(2, '0')}:${finalMinute.toString().padStart(2, '0')}:${finalSecond.toString().padStart(2, '0')}`;
    
    // Start with UTC interpretation of this time (treat as UTC first)
    let targetDate = new Date(targetDateStr + 'Z'); // Add Z to force UTC interpretation
    
    // Format date/time components in the target timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    
    // Iteratively adjust to get the exact time in target timezone
    // We want targetDate to represent finalYear-finalMonth-finalDay finalHour:finalMinute:finalSecond in target timezone
    let attempts = 0;
    const maxAttempts = 10;
    while (attempts < maxAttempts) {
      const parts = formatter.formatToParts(targetDate);
      const tzYear = parseInt(parts.find(p => p.type === 'year')?.value || '0', 10);
      const tzMonth = parseInt(parts.find(p => p.type === 'month')?.value || '0', 10);
      const tzDay = parseInt(parts.find(p => p.type === 'day')?.value || '0', 10);
      const tzHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
      const tzMinute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
      const tzSecond = parseInt(parts.find(p => p.type === 'second')?.value || '0', 10);
      
      // Check if we have the right date and time
      if (tzYear === finalYear && tzMonth === finalMonth && tzDay === finalDay && 
          tzHour === finalHour && tzMinute === finalMinute && tzSecond === finalSecond) {
        break;
      }
      
      // Calculate differences
      const yearDiff = finalYear - tzYear;
      const monthDiff = finalMonth - tzMonth;
      const dayDiff = finalDay - tzDay;
      const hourDiff = finalHour - tzHour;
      const minuteDiff = finalMinute - tzMinute;
      const secondDiff = finalSecond - tzSecond;
      
      // Adjust the date (convert differences to milliseconds)
      const totalDiffMs = 
        yearDiff * 365 * 24 * 60 * 60 * 1000 + // Approximate year diff
        monthDiff * 30 * 24 * 60 * 60 * 1000 + // Approximate month diff
        dayDiff * 24 * 60 * 60 * 1000 +
        hourDiff * 60 * 60 * 1000 +
        minuteDiff * 60 * 1000 +
        secondDiff * 1000;
      
      targetDate = new Date(targetDate.getTime() + totalDiffMs);
      attempts++;
    }
    
    // Format time string as UTC with Z (matching portal format: "2025-12-13T10:55:00.000Z")
    // targetDate is already in UTC, so format it directly
    const utcYear = targetDate.getUTCFullYear();
    const utcMonth = String(targetDate.getUTCMonth() + 1).padStart(2, '0');
    const utcDay = String(targetDate.getUTCDate()).padStart(2, '0');
    const utcHour = String(targetDate.getUTCHours()).padStart(2, '0');
    const utcMinute = String(targetDate.getUTCMinutes()).padStart(2, '0');
    const utcSecond = String(targetDate.getUTCSeconds()).padStart(2, '0');
    const utcMs = String(targetDate.getUTCMilliseconds()).padStart(3, '0');
    
    const result = `${utcYear}-${utcMonth}-${utcDay}T${utcHour}:${utcMinute}:${utcSecond}.${utcMs}Z`;
    console.log(`[formatTimeWithTimezone] Formatted time: ${result} (from ${timeString}, timezone: ${timezone})`);
    return result;
  } catch (error) {
    console.warn('[formatTimeWithTimezone] Error formatting time:', error, { timeString, timezone });
    return timeString;
  }
}

/**
 * Export ride data from Atlas
 * 
 * @param ride - RideSchema to export
 * @returns Export result with validated data
 */
export function exportRideData(ride: RideSchema): RideExportResult {
  // Normalize passenger data first
  const normalizedRide = normalizeRidePassengers(ride);
  
  const validation = validateRideExport(normalizedRide);
  
  if (!validation.isValid) {
    return validation;
  }

  // Extract trip type from request_type or default to leaveAt
  const tripType: 'leaveAt' | 'arriveBy' = 
    normalizedRide.request_type === 'arriveBy' ? 'arriveBy' : 'leaveAt';

  return {
    ...validation,
    validatedData: {
      ride: normalizedRide,
      tripType,
      // These will be populated by the upload hook after location search
      pickupLocation: {
        latitude: normalizedRide.from_latitude,
        longitude: normalizedRide.from_longitude,
        address: normalizedRide.from_address || 'Pickup Location',
        label: normalizedRide.from_address || 'Pickup Location',
      },
      dropoffLocation: {
        latitude: normalizedRide.to_latitude,
        longitude: normalizedRide.to_longitude,
        address: normalizedRide.to_address || 'Dropoff Location',
        label: normalizedRide.to_address || 'Dropoff Location',
      },
      userId: normalizedRide.user_id || normalizedRide.external_user_reference_id || '',
      time: normalizedRide.request_time || '',
    },
  };
}

